var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AuthenticationResult, AuthenticationStatus, AuthenticationReason, AuthenticationSubject } from '@mc-alberta/types';
import merge from 'lodash.merge';
import { getAuthMethodTypesForStrategy } from '../sdk/utils/getAuthMethodTypes';
import { AuthenticationStrategy } from '../types/enums/AuthenticationStrategy';
import { Kpi } from '../loggers/Kpi';
import { HttpHeader } from '../types/enums/http-header.enum';
import { axiosService } from '../services/axios.service';
import { State } from '../sdk/state';
import { KPI_RESOURCE, VERIFICATION_RESULT } from '../constants';
import { getConsumerId } from '../sdk/utils/getConsumerId';
export class Adapter extends State {
    /**
     * True if a verification has occurred.
     *
     * Note that this does not guarantee that an ID&V (identity verification) was performed.
     * For that, use {@link isIdentityVerificationPerformed}.
     */
    get isVerified() {
        return this.getVerificationData().some(({ verificationResults }) => {
            return verificationResults === VERIFICATION_RESULT.VERIFIED;
        });
    }
    /** The actively-selected authentication method. */
    get authenticationMethod() {
        return this.authenticationMethods.length === 1
            ? this.authenticationMethods[0]
            : this.authenticationMethods[this.selectedMethodIndex];
    }
    /** True if the strategy can be used for authentication. */
    get isAuthenticatable() {
        return ((this.authenticationStatus === AuthenticationStatus.COMPLETE ||
            this.authenticationMethods.length > 0) &&
            this.authenticationReasons.reduce((isUsable, reason) => {
                return this.supportedReasons.includes(reason) || isUsable;
            }, false));
    }
    get authenticationContext() {
        return this.getAuthenticationContext({
            authenticationReasons: this.authenticationReasons
        });
    }
    constructor(params) {
        super(params);
        this.awaiting = {};
        /** Current status of the active authentication session. */
        this.authenticationStatus = AuthenticationStatus.NOT_SUPPORTED;
        /** Whether or not authentication has successfully completed. */
        this.authenticationResult = AuthenticationResult.NOT_AUTHENTICATED;
        /** List of usable authentication methods belonging to this strategy. */
        this.authenticationMethods = [];
        /** The index of the actively-selected authentication method. */
        this.selectedMethodIndex = -1;
        /** Whether or not preloadSession() was called. */
        this.isPreloaded = false;
        /** Whether or not ID&V was performed for this specific strategy. */
        this.isIdentityVerificationPerformed = false;
        /** List of supported authentication reasons for this strategy. */
        this.supportedReasons = Object.values(AuthenticationReason);
        /**
         * Returns the current, locally-cached authentication session status.
         * This does not make a request to the authentication service.
         */
        this.getSession = () => Promise.resolve({
            authenticationResult: this.authenticationResult,
            authenticationStatus: this.authenticationStatus,
            assuranceData: this.assuranceData,
            error: this.error,
            methodAttributes: this.outputMethodAttributes
        });
        /**
         * Destroys the current adapter session.
         *
         * ### Caution: this is an atomic operation. Use at own risk.
         * For returning transient non-supported state, use {@link getNotSupportedResult}
         */
        this.destroySession = (error) => {
            this.authenticationStatus = AuthenticationStatus.NOT_SUPPORTED;
            this.authenticationResult = AuthenticationResult.NOT_AUTHENTICATED;
            if (error) {
                this.kpi.queueError(error);
            }
            return this.getSession();
        };
        /**
         * Resets the current authentication session back to its original state.
         */
        this.clearSession = () => {
            this.authenticationStatus = this.isAuthenticatable
                ? AuthenticationStatus.PENDING
                : AuthenticationStatus.NOT_SUPPORTED;
            this.authenticationResult = AuthenticationResult.NOT_AUTHENTICATED;
            this.isIdentityVerificationPerformed = false;
            this.isPreloaded = false;
            delete this.authenticationSessionId;
            delete this.assuranceData;
            delete this.error;
            return this.getSession();
        };
        /**
         * Makes a POST API request using the Axios adapter instance.
         */
        this.apiRequest = (endpoint, payload, headers = {}) => __awaiter(this, void 0, void 0, function* () {
            const { data } = yield axiosService.post(endpoint, Object.assign({ accountReference: this.accountReference, authenticationContext: this.authenticationContext, srcCorrelationId: this.srcCorrelationId, srcClientId: this.srcClientId, srciTransactionId: this.srciTransactionId, serviceId: this.serviceId, authenticationSessionId: this.authenticationSessionId }, payload), {
                headers: Object.assign({ [HttpHeader.X_SRC_CX_FLOW_ID]: this.traceId }, headers)
            });
            return data;
        });
        /**
         * Adapter-specific KPI meta data.
         */
        this.kpiMeta = () => {
            var _a;
            return (Object.assign(Object.assign(Object.assign({}, this.consumerKpiMeta()), this.cardKpiMeta()), { resource: ((_a = this.authenticationMethod) === null || _a === void 0 ? void 0 : _a.authenticationSubject) === AuthenticationSubject.CONSUMER
                    ? KPI_RESOURCE.CONSUMER
                    : KPI_RESOURCE.CARD }));
        };
        /**
         * KPI Formatter function
         */
        this.onKpiSuccess = () => {
            var _a, _b, _c;
            const strategyMap = {
                [AuthenticationStrategy.THREE_DS]: 'threeDs',
                [AuthenticationStrategy.MDES]: 'consent',
                [AuthenticationStrategy.FIDO2]: 'fido',
                [AuthenticationStrategy.LVLR]: 'lvlr',
                [AuthenticationStrategy.CSC_VALIDATION]: 'cvc',
                [AuthenticationStrategy.CONSUMER_OTP]: 'consumer'
            };
            return Object.assign({}, (((_a = this.authenticationMethod) === null || _a === void 0 ? void 0 : _a.authenticationSubject) !== AuthenticationSubject.CONSUMER && {
                cardData: {
                    cardId: (_b = this.accountReference) === null || _b === void 0 ? void 0 : _b.srcDigitalCardId,
                    cardVerificationData: {
                        cardAuthRequestReason: (_c = this.authenticationReasons) === null || _c === void 0 ? void 0 : _c[0],
                        cardAuthVerificationStatus: this.authenticationStatus,
                        cardAuthVerificationResult: this.authenticationResult,
                        cardAuthVerificationId: this.authenticationSessionId,
                        [`${strategyMap[this.strategy]}AuthVerificationSupported`]: true
                    }
                }
            }));
        };
        this.cardKpiMeta = () => {
            var _a, _b, _c, _d;
            if (((_a = this.authenticationMethod) === null || _a === void 0 ? void 0 : _a.authenticationSubject) === AuthenticationSubject.CONSUMER) {
                return {};
            }
            return {
                cardData: {
                    cardId: (_b = this.accountReference) === null || _b === void 0 ? void 0 : _b.srcDigitalCardId,
                    cardVerificationData: {
                        cardAuthVerificationType: (_c = this.authenticationMethod) === null || _c === void 0 ? void 0 : _c.authenticationMethodType,
                        cardAuthRequestReason: (_d = this.authenticationReasons) === null || _d === void 0 ? void 0 : _d[0]
                    }
                }
            };
        };
        this.consumerKpiMeta = () => {
            var _a, _b, _c, _d, _e, _f;
            if (((_a = this.authenticationMethod) === null || _a === void 0 ? void 0 : _a.authenticationSubject) !== AuthenticationSubject.CONSUMER) {
                return {};
            }
            return {
                consumerData: {
                    consumerId: getConsumerId((_c = (_b = this.assuranceData) === null || _b === void 0 ? void 0 : _b.verificationData[0]) === null || _c === void 0 ? void 0 : _c.additionalData),
                    identityType: (_d = this.accountReference.consumerIdentity) === null || _d === void 0 ? void 0 : _d.identityType,
                    consumerVerificationData: {
                        consumerAuthRequestReason: (_e = this.authenticationReasons) === null || _e === void 0 ? void 0 : _e[0],
                        consumerAuthVerificationType: (_f = this.authenticationMethod) === null || _f === void 0 ? void 0 : _f.authenticationMethodType,
                        consumerAuthVerificationStatus: this.authenticationStatus,
                        consumerAuthVerificationResult: this.authenticationResult
                    }
                }
            };
        };
        this.authenticationReasons = params.authenticationReasons;
        this.accountReference = params.accountReference;
        this.kpi = new Kpi(params, this.kpiMeta.bind(this));
    }
    /**
     * Applies the given authentication methods for this adapter.
     */
    applyAuthenticationMethods(authenticationMethods) {
        const types = getAuthMethodTypesForStrategy(this.strategy);
        /**
         * Checking if there are existing auth methods first helps us prevent the session from being
         * cleared when we reload auth methods (i.e. when a user selects a different OTP channel).
         * TODO: This is a temporary fix and should be revisited later.
         *
         * TODO: should not need to check authenticationMethods truthiness...
         */
        if (!this.authenticationMethods.length) {
            this.authenticationMethods = authenticationMethods.filter((method) => {
                return types.includes(method.authenticationMethodType);
            });
            if (this.authenticationMethods.length === 1) {
                this.selectedMethodIndex = 0;
            }
            this.clearSession();
        }
    }
    getLookupContext() {
        return __awaiter(this, void 0, void 0, function* () {
            return {};
        });
    }
    /**
     * Set strategy-specific output method attributes.
     *
     * This is what we'll return to the consumer.
     */
    setOutputMethodAttributes(payload) {
        this.outputMethodAttributes = merge(this.outputMethodAttributes, payload);
    }
    /**
     * Returns the verification data generated for this strategy.
     */
    getVerificationData() {
        var _a;
        return ((_a = this.assuranceData) === null || _a === void 0 ? void 0 : _a.verificationData) || [];
    }
    /**
     * Applies the given verification data onto this strategy.
     */
    setVerificationData(verificationData) {
        var _a;
        const applicableVerifications = verificationData.filter((verificationData) => {
            // do not overwrite the current successful verification unless this one is also verified
            if (this.isVerified &&
                verificationData.verificationResults !== VERIFICATION_RESULT.VERIFIED) {
                return false;
            }
            // ensure that this method is applicable to this strategy
            return this.verificationMethods.includes(verificationData.verificationMethod);
        });
        if (applicableVerifications.length) {
            this.assuranceData = {
                verificationData: [
                    ...(((_a = this.assuranceData) === null || _a === void 0 ? void 0 : _a.verificationData) || []),
                    ...applicableVerifications
                ]
            };
            if (this.isVerified) {
                this.authenticationStatus = AuthenticationStatus.COMPLETE;
                this.authenticationResult = AuthenticationResult.AUTHENTICATED;
            }
        }
    }
    /**
     * Fetches the current authentication session information from the authentication service.
     *
     * @param methodAttributes - additional method-specific attributes to send with the request
     * @param handleError - whether or not to automatically convert XHR errors into `NOT_SUPPORTED`
     * @param payload - any additional headers to send to auth provider
     */
    refreshSession(methodAttributes = {}, payload = {}, headers = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if ([
                AuthenticationStatus.NOT_SUPPORTED,
                AuthenticationStatus.EXPIRED,
                AuthenticationStatus.CHALLENGE_FAILED,
                AuthenticationStatus.CANCELLED
            ].includes(this.authenticationStatus)) {
                return this.getSession();
            }
            try {
                delete this.error;
                const method = this.authenticationMethod;
                const data = yield this.apiRequest('/api/accountholder/authentications/authenticate', Object.assign(Object.assign({}, payload), { authenticationMethod: Object.assign(Object.assign({}, this.authenticationMethod), { methodAttributes: Object.assign(Object.assign({}, this.authenticationMethod.methodAttributes), methodAttributes) }) }), headers);
                this.assuranceData = data.assuranceData;
                this.authenticationResult = data.authenticationResult;
                this.authenticationStatus = data.authenticationStatus;
                this.authenticationSessionId = data.authenticationSessionId;
                method.methodAttributes = merge(method.methodAttributes, data.methodAttributes);
            }
            catch (error) {
                this.error = error;
                return this.destroySession(error);
            }
            return this.getSession();
        });
    }
    /**
     * Binds the successfully-authenticated session (e.g., 3DS, FIDO2 authenticator)
     * to the account reference in question.
     *
     * Session binding will only occur if the strategy in question performed ID&V.
     *
     * @returns true if the session was successfully bound; false otherwise
     */
    bindSession() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isIdentityVerificationPerformed) {
                try {
                    yield this.apiRequest('/api/accountholder/authentications/token/bind', {
                        srcDigitalCardId: this.accountReference.srcDigitalCardId,
                        assuranceData: this.assuranceData
                    });
                    return true;
                }
                catch (error) {
                    // TODO: probably good to log this error
                }
            }
            return false;
        });
    }
    /**
     * Returns a transient, non-supported authentication state.
     */
    getNotSupportedResult() {
        return Promise.resolve({
            authenticationStatus: AuthenticationStatus.NOT_SUPPORTED,
            authenticationResult: AuthenticationResult.NOT_AUTHENTICATED
        });
    }
    /**
     * {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#description | JSON serializer}.
     */
    toJSON() {
        return {
            accountReference: this.accountReference,
            assuranceData: this.assuranceData,
            authenticationMethods: this.authenticationMethods,
            authenticationReasons: this.authenticationReasons,
            authenticationResult: this.authenticationResult,
            authenticationSessionId: this.authenticationSessionId,
            authenticationStatus: this.authenticationStatus,
            authorization: this.authorization,
            dasClientId: this.dasClientId,
            dasHost: this.dasHost,
            dpaData: this.dpaData,
            dpaTransactionOptions: this.dpaTransactionOptions,
            isIdentityVerificationPerformed: this.isIdentityVerificationPerformed,
            isPreloaded: this.isPreloaded,
            namespace: this.namespace,
            selectedMethodIndex: this.selectedMethodIndex,
            serviceId: this.serviceId,
            srcClientId: this.srcClientId,
            srcCorrelationId: this.srcCorrelationId,
            srcDpaId: this.srcDpaId,
            srciTransactionId: this.srciTransactionId,
            traceId: this.traceId
        };
    }
}
